import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  reset as resetKeywordLandingURL,
  setErrors,
} from 'features/dashboard/operation/keywordLandingUrlEdit/keywordLandingUrlEditSlice'
import { useCallback, useEffect, useState } from 'react'
import useValidator from 'utils/hooks/useValidator'
import { success } from 'features/toast/Toast'
import { useErrorToast } from 'utils/hooks/useErrorToast'
import {
  isPresent,
  isValidUrl,
  reduceEntries,
  separateUrlProtocol,
} from 'utils'
import api from 'api/axios/foka'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import {
  exitEditable,
  KeywordTableEditTypes,
  setSelectedIds,
} from 'features/dashboard/table/keyword/keywordTableSlice'

const useKeywordLandingUrlEdit = () => {
  const dispatch = useDispatch()
  const {
    table: {
      keywordTable: { editable, editType },
    },
    operation: {
      keywordLandingURL: { keywords, hasLandingURL, isIndividual, siteSetting },
    },
  } = useSelector(state => state.dashboard, shallowEqual)
  const [apiError, setApiError] = useState()
  useErrorToast({
    error: apiError,
    handleClearError: () => setApiError(null),
  })
  const isActive = editable && editType === KeywordTableEditTypes.landingUrl

  const { errors: validationErrors, validate } = useValidator(
    () =>
      hasLandingURL
        ? Object.entries(keywords)
            .map(([id, { pcSite, mobileSite, unifiedSite }]) => {
              const needPcOnly =
                siteSetting.isIndividual && !siteSetting.hasMobileSite
              const needMobileOnly =
                siteSetting.isIndividual && !siteSetting.hasPcSite
              const needBoth =
                (siteSetting.isIndividual &&
                  siteSetting.hasMobileSite &&
                  siteSetting.hasPcSite) ||
                (siteSetting.isIndividual === false && isIndividual === true)
              return [
                id,
                {
                  ...(isIndividual
                    ? {
                        pcSite:
                          !needMobileOnly &&
                          ((needPcOnly || needBoth
                            ? !isPresent(pcSite) && 'PC URL을 입력해주세요.'
                            : !isPresent(pcSite) &&
                              !isPresent(mobileSite) &&
                              '모바일/PC URL중 적어도 하나는 입력해야 합니다.') ||
                            (isPresent(pcSite) &&
                              !isValidUrl(pcSite) &&
                              '올바른 URL 주소가 아닙니다.')),
                        mobileSite:
                          !needPcOnly &&
                          ((needMobileOnly || needBoth
                            ? !isPresent(mobileSite) &&
                              '모바일 URL을 입력해주세요.'
                            : !isPresent(pcSite) &&
                              !isPresent(mobileSite) &&
                              '모바일/PC URL중 적어도 하나는 입력해야 합니다.') ||
                            (isPresent(mobileSite) &&
                              !isValidUrl(mobileSite) &&
                              '올바른 URL 주소가 아닙니다.')),
                      }
                    : {
                        unifiedSite:
                          (!isPresent(unifiedSite) &&
                            '웹사이트 URL을 입력해주세요.') ||
                          (!isValidUrl(unifiedSite) &&
                            '올바른 URL 주소가 아닙니다.'),
                      }),
                },
              ]
            })
            .filter(
              ([, { pcSite, mobileSite, unifiedSite }]) =>
                pcSite || mobileSite || unifiedSite
            )
            .reduce(reduceEntries, {})
        : {},
    [keywords]
  )

  useEffect(() => {
    dispatch(setErrors(validationErrors))
  }, [dispatch, validationErrors])

  const reset = useCallback(
    () => dispatch(resetKeywordLandingURL()),
    [dispatch]
  )

  const save = useCallback(async () => {
    try {
      await validate()
    } catch (e) {
      return
    }
    try {
      const commands = hasLandingURL
        ? Object.entries(keywords).map(
            ([keywordId, { pcSite, mobileSite, unifiedSite }]) => ({
              keywordId,
              landingUrl: hasLandingURL
                ? {
                    ...(isIndividual
                      ? {
                          individualSettingYn: 'Y',
                          pcLandingUrl: pcSite
                            ? (([protocol, url]) => ({
                                url,
                                protocol: protocol.toUpperCase(),
                              }))(separateUrlProtocol(pcSite))
                            : null,
                          mobileLandingUrl: mobileSite
                            ? (([protocol, url]) => ({
                                url,
                                protocol: protocol.toUpperCase(),
                              }))(separateUrlProtocol(mobileSite))
                            : null,
                        }
                      : {
                          individualSettingYn: 'N',
                          pcLandingUrl: unifiedSite
                            ? (([protocol, url]) => ({
                                url,
                                protocol: protocol.toUpperCase(),
                              }))(separateUrlProtocol(unifiedSite))
                            : null,
                        }),
                  }
                : null,
            })
          )
        : Object.keys(keywords).map(keywordId => ({
            keywordId,
            landingUrl: null,
          }))
      await api.dashboard.modifyLandingURL(commands)
      success('키워드 랜딩 URL 변경사항이 적용되었습니다.')
      batch(() => {
        dispatch(exitEditable())
        dispatch(resetKeywordLandingURL())
        dispatch(setSelectedIds([]))
        dispatch(refreshDashboard())
      })
    } catch (e) {
      setApiError(e)
    }
  }, [dispatch, hasLandingURL, isIndividual, keywords, validate])

  return {
    isActive,
    reset,
    save,
  }
}
export default useKeywordLandingUrlEdit
